import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!email) {
      setFormError("Please enter your email address.");
      return;
    }
    setLoading(true);
    const result = await forgotPassword(email);
    setLoading(false);
    // We always show the success state — the backend deliberately doesn't
    // reveal whether the email exists, so we don't leak it on the UI either.
    if (result.success || result.error) {
      setSent(true);
    } else {
      setFormError(result.error || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
        data-testid="forgot-password-page"
      >
        {/* Logo */}
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/05veeg6r_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%282%29.png"
              alt="LocalBest.ai"
              className="h-14 object-contain"
            />
          </Link>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 shadow-2xl backdrop-blur">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
                  Forgot your password?
                </h2>
                <p className="text-[#94A3B8] text-sm">
                  Enter the email on your LocalBest.ai account and we'll send you a secure reset link.
                </p>
              </div>

              {formError && (
                <div className="mb-5 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg">
                  <p className="text-[#EF4444] text-sm" data-testid="forgot-error">{formError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                      data-testid="forgot-email-input"
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#8B5CF6] hover:to-[#F472B6] text-white font-semibold text-base"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending reset link…
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-sent-state">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-3">
                Check your inbox
              </h2>
              <p className="text-[#94A3B8] text-sm leading-relaxed">
                If an account exists for{" "}
                <span className="text-white font-medium">{email}</span>, we just sent a secure
                password reset link to that email. The link is valid for 1 hour.
              </p>
              <p className="text-[#64748B] text-xs mt-4">
                Didn't get it? Check your spam folder, or wait a minute and try again.
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link
              to="/auth/login"
              className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white transition-colors"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
